import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import NumericInput from 'react-numeric-input';
import { MdCancel } from 'react-icons/md';
import Dropdown from 'react-dropdown';
import ReactApexChart from "react-apexcharts";
import { useAppContext } from '../context/AppContext';
import { deriveProgramAndAgeGroup, getNorm } from '../utils/normHelpers';
import getInputGroupings from '../Scripts/_getInputGroupings';
import createRadarChart from '../Scripts/_createAthleteAnalysisChart.js';
import calculate_percentile from "../Scripts/_calculatedPercentile";

const InputColumn = styled.div`
  flex-basis: 50%;
  flex-grow: 0;
  flex-shrink: 0;
  justify-content: right;
  max-width: 50%;
  width: 50%;
  margin-left: 5%;
  margin-right: 2%;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    max-width: 100%;
    width: 100%;
    margin: 0 0 ${props => props.theme.spacing.medium} 0;
  }
`;

const ChartColumn = styled.div`
  flex-basis: 50%;
  flex-grow: 1;
  flex-shrink: 0;
  align-items: left;
  justify-content: left;
  min-width: 50%;
  max-width: 50%;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    min-width: 100%;
    max-width: 100%;
    padding: ${props => props.theme.spacing.medium} 0;
    margin: 0;
  }
`;

const NotesColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0; // Important for flex containers to prevent overflow

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing.small};
    margin-left: 0;
  }
`;

const NotesLabel = styled.label`
  font-size: ${props => props.theme.fontSizes.large};
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.small};
`;

const NotesTextarea = styled.textarea`
  width: 100%;
  height: 25vh;
  font-size: ${props => props.theme.fontSizes.medium};
  resize: none;
  background-color: ${props => props.theme.colors.secondary};
  color: ${props => props.theme.colors.text};
  border: 1px solid ${props => props.theme.colors.secondary};
  border-radius: 5px;
  padding: ${props => props.theme.spacing.small};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    height: 30vh;
  }
`;

const InputGroupingContainer = styled.div`
  display: flex;
  width: 100%;
  gap: ${props => props.theme.spacing.medium};
  padding: ${props => props.theme.spacing.medium};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: column;
    padding: ${props => props.theme.spacing.small};
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  font-size: 12px;
`;

const Label = styled.label`
  padding-left: 5px;
`;

const StyledNumericInput = styled(NumericInput)`
  && {
    outline: none;
    border: none;
    padding: 6px 8px;
    width: 125px;
    font-size: 12px;
    background-color: rgb(50, 50, 50);
    color: white;
    height: 28px;
    margin-top: 5px;
    border-radius: 20px !important;
    text-align: center;
  }
    &:disabled {
      opacity: 1;
      color: white;
      -webkit-text-fill-color: white;
      cursor: not-allowed;
    }

    /* Ensure input content doesn't dim */
    & input {
      opacity: 1;
      color: white;
      -webkit-text-fill-color: white;
    }

    & input:disabled {
      opacity: 1;
      color: white;
      -webkit-text-fill-color: white;
    }
      
`;

const NormOutput = styled(StyledNumericInput)`
  && {
    width: 125px;

    &::placeholder {
      color: white;
      text-shadow: none;
      opacity: 1;
    }
  }
`;

const InputFieldWrapper = styled.div`
  display: flex;
  padding: 10px;
  align-items: center;
  gap: ${props => props.theme.spacing.small};
`;

const InputSection = styled.div`
  position: relative;
  width: 125px; // Match StyledNumericInput width
`;

const WarningText = styled.p`
  margin: 0;
  padding: 5px;
  color: ${props => props.theme.colors.primary};
  font-size: 9px;
`;

const OptOutButton = styled.button`
  -webkit-appearance: none;
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
  border: none;
  width: 25px;
  height: 25px;
  background-color: ${props => props.pressed ? '#474747' : 'rgb(51, 51, 51)'};
  border-radius: 20px;
  text-align: center;
  align-items: center;
  justify-content: center;
  font-size: 25px;
  padding: 2px;
  margin-left: 10px;
  margin-right: 40px;
  display: flex;
  color: ${props => props.theme.colors.primary};
  transition: background-color 0.2s ease;
  opacity: ${props => props.isVisible ? 1 : 0};
  pointer-events: ${props => props.isVisible ? 'auto' : 'none'};
  
  ${props => props.pressed && `
    box-shadow: inset 3px 3px 7px #353535,
                inset -3px -3px 7px #595959;
  `}
`;

const StyledDropdown = styled(Dropdown)`
  width: 125px; // Match StyledNumericInput width
  
  .Dropdown-control {
    border: none;
    padding: 3px 5px;
    margin-top: 5px;
    font: 14px 'Montserrat', sans-serif;
    color: ${props => props.theme.colors.primary};
    background: rgb(51, 51, 51);
    height: 20px; // Match StyledNumericInput height
    display: flex;
    align-items: center;
  }

  .Dropdown-menu {
    border-radius: 10px;
    margin-top: 5px;
    font: 14px 'Montserrat', sans-serif;
    color: white;
    border: none;
    text-align: center;
    background-color: ${props => props.theme.colors.primary};
  }

  .Dropdown-option {
    background-color: white;
    color: ${props => props.theme.colors.background};
    &:hover {
      background-color: ${props => props.theme.colors.primary};
    }
  }
`;

const InputField = React.memo(function InputField({ 
  currentStepInputTag, 
  inputSettings, 
  norm, 
  percentile, 
  inputValue, 
  optOutReason, 
  captureModeIsActive, 
  editingEvalModeIsActive, 
  handleInputValueChange, 
  handleInputValueValidityChange, 
  handleOptOutReasonChange, 
  showNorms, 
  currentStepName 
}) {
  const { inputStringName, minValue: initialMinValue, maxValue: initialMaxValue, precision } = inputSettings;

  const handleValueChange = useCallback((valueAsNumber, valueAsString) => {
    if (valueAsString === '') {
      handleInputValueChange(currentStepInputTag, '');
    } else if (!isNaN(valueAsNumber)) {
      handleInputValueChange(currentStepInputTag, valueAsNumber);
    } else {
      handleInputValueChange(currentStepInputTag, valueAsString);
    }
  }, [currentStepInputTag, handleInputValueChange]);

  const handleBlur = useCallback(() => {
    handleInputValueValidityChange(currentStepInputTag, (inputValue >= initialMinValue) && (inputValue <= initialMaxValue));
  }, [currentStepInputTag, inputValue, initialMinValue, initialMaxValue, handleInputValueValidityChange]);

  const optOutIsNull = optOutReason === 'null';
  const inputValueIsInvalid = typeof inputValue != 'number' || inputValue === -99999;

  const displayValue = inputValueIsInvalid ? '' : (inputValue === '' ? '' : parseFloat(inputValue));

  const optOutReasons = ['Injury', 'Prohibited', 'Other'];

  const isIntegerTest = ['modThomas_psoas_R', 'modThomas_ITband_R', 'modThomas_quads_R', 'modThomas_psoas_L', 'modThomas_ITband_L', 'modThomas_quads_L', 
                         'yBalance_shldr_R', 'yBalance_hip_R', 'yBalance_knee_R', 'yBalance_shldr_L', 'yBalance_hip_L', 'yBalance_knee_L',
                         'ohsquat', 'hinge', 'squat', 'pushup', 'aslr_R', 'aslr_L', 'shoulderMob_R', 'shoulderMob_L'].includes(currentStepInputTag);

  const displayNorms = showNorms && !isIntegerTest && currentStepName !== 'Anthropometry';

  return (
    <InputFieldWrapper>
      <InputContainer>
        <Label optOutIsNull={optOutIsNull}>{inputStringName}</Label>
        <InputSection>
          {optOutIsNull ? (
            <StyledNumericInput 
              style={{
                btnUp: {
                  display: 'none',
                },
                btnDown: {
                  display: 'none',
                },
              }}
              //min={initialMinValue} 
              //max={initialMaxValue} 
              value={displayValue} 
              step={1 / Math.pow(10, precision)} 
              precision={precision}
              strict={false}
              inputMode="numeric"
              onChange={handleValueChange}
              onBlur={handleBlur}
              disabled={!captureModeIsActive && !editingEvalModeIsActive}
              placeholder={(!captureModeIsActive && !editingEvalModeIsActive) ? "-" : "Enter a number"}
              format={val => {
                if (val === '' || val === null || val === undefined) return '';
                if (typeof val === 'number') return val.toFixed(precision);
                if (typeof val === 'string') {
                  const num = parseFloat(val);
                  return isNaN(num) ? val : num.toFixed(precision);
                }
                return val.toString();
              }}
            />
          ) : (
            <StyledDropdown
              options={optOutReasons}
              value={optOutReason} 
              onChange={(selection) => handleOptOutReasonChange(currentStepInputTag, selection.value)}
              disabled={!captureModeIsActive && !editingEvalModeIsActive}
            />
          )}
          {(!inputValueIsInvalid && (inputValue < initialMinValue || inputValue > initialMaxValue)) && 
            <WarningText>{`Range (${initialMinValue.toFixed(precision)} - ${initialMaxValue.toFixed(precision)})`}</WarningText>
          }
        </InputSection>
      </InputContainer>
      
      <OptOutButton 
        pressed={!optOutIsNull} 
        isVisible={captureModeIsActive || editingEvalModeIsActive}
        onClick={() => {
          handleInputValueChange(currentStepInputTag, -99999);
          handleOptOutReasonChange(currentStepInputTag, optOutIsNull ? 'Injury' : 'null');
          handleInputValueValidityChange(currentStepInputTag, !optOutIsNull);
        }}
      >
        <MdCancel />
      </OptOutButton>
  
      <>
        <InputContainer style={{paddingRight:'25px'}}>
          <Label>Norm mean ± SD</Label>
          <NormOutput 
            style={{
              btnUp: {
                display: 'none',
              },
              btnDown: {
                display: 'none',
              },
            }}
            disabled={true}
            strict={false}
            inputMode="text"
            placeholder={displayNorms ? norm.mean ? `${norm.mean.toFixed(precision)} ± ${norm.sd.toFixed(precision)}` : 'None available' : ''} 
          />
        </InputContainer>
        <InputContainer style={{paddingRight:'25px'}}>
          <Label>Percentile [%]</Label>
          <NormOutput           
            style={{
              btnUp: {
                display: 'none',
              },
              btnDown: {
                display: 'none',
              },
            }}
            disabled={true}
            strict={false}
            inputMode="text"
            placeholder={displayNorms ? percentile !== null ? percentile.toFixed(1) : ' - ' : ''} 
          />
        </InputContainer>
      </>

    </InputFieldWrapper>
  );
});

const InputGroupings = ({ showNorms, showNotes }) => {
  const {
    activeStep,
    inputValuesObject,
    setInputValuesObject,
    norms,
    captureModeIsActive,
    editingEvalModeIsActive,
    athlete,
    INPUT_SETTINGS
  } = useAppContext();

  const athleteGender = athlete?.gender;
  const athleteProgram = athlete?.program;
  const athleteGroup = athlete?.group;

  const inputGroupingsObject = useMemo(() => getInputGroupings('Gold', athleteProgram, captureModeIsActive) || [], [athleteProgram, captureModeIsActive]);
  const steps = useMemo(() => inputGroupingsObject.map(group => group.groupName), [inputGroupingsObject]);

  const currentStepName = useMemo(() => activeStep <= steps.length - 1 ? steps[activeStep] : 'Finished', [activeStep, steps]);
  const currentStepInputGroup = useMemo(() => activeStep <= steps.length - 1 && inputGroupingsObject[activeStep] ? inputGroupingsObject[activeStep].inputTags : [], [activeStep, inputGroupingsObject, steps.length]);

  const { programKey, ageGroup } = useMemo(() => deriveProgramAndAgeGroup(
    athleteProgram ?? '',
    athleteGroup ?? '',
    athlete?.age ?? null
  ), [athleteProgram, athleteGroup, athlete]);

  const ensureFieldShape = useCallback((prev, key) => {
    const existing = prev?.[key];
    if (existing && typeof existing === 'object' && Object.prototype.hasOwnProperty.call(existing, 'value')) {
      return existing;
    }
    return { value: '', isValid: false, optOutReason: 'null' };
  }, []);

  const handleInputValueChange = useCallback((currentInputName, value) => {
    setInputValuesObject(prev => {
      const baseField = ensureFieldShape(prev, currentInputName);
      return {
        ...prev,
        [currentInputName]: { ...baseField, value }
      };
    });
  }, [setInputValuesObject, ensureFieldShape]);

  const handleInputValueValidityChange = useCallback((currentInputName, isValidFlag) => {
    setInputValuesObject(prev => {
      const baseField = ensureFieldShape(prev, currentInputName);
      return {
        ...prev,
        [currentInputName]: { ...baseField, isValid: true || isValidFlag }
      };
    });
  }, [setInputValuesObject, ensureFieldShape]);

  const handleOptOutReasonChange = useCallback((currentInputName, optOutReasonString) => {
    setInputValuesObject(prev => {
      const baseField = ensureFieldShape(prev, currentInputName);
      return {
        ...prev,
        [currentInputName]: { ...baseField, optOutReason: optOutReasonString }
      };
    });
  }, [setInputValuesObject, ensureFieldShape]);

  const renderInputFields = useMemo(() => {
    return currentStepInputGroup.map((currentStepInputTag) => {
      const thisInputsSettings = INPUT_SETTINGS.find(setting => setting.tag === currentStepInputTag);
      if (!thisInputsSettings) {
        console.warn(`Missing INPUT_SETTINGS entry for tag '${currentStepInputTag}'`);
        return <WarningText key={currentStepInputTag}>Missing config for {currentStepInputTag}</WarningText>;
      }
  
      const tag = inputValuesObject?.[currentStepInputTag];
      const safeTag = tag ?? { value: '', isValid: false, optOutReason: 'null' };
  
      const norm = getNorm(currentStepInputTag, programKey, ageGroup, athleteGender, norms);
  
      const calculate_percent = norm.mean && safeTag.isValid && showNorms;

      let percentile = calculate_percent ? calculate_percentile(safeTag.value, norm.mean, norm.sd) : null;
      
      const currentInputName = thisInputsSettings.inputStringName;
      //alert(currentStepInputTag + ': ' + percentile);
      if (percentile && (currentInputName.includes('[s]') || currentInputName.includes('[sec]')))
        percentile = 100.0 - percentile;
        
      return (
        <React.Fragment key={currentStepInputTag}>
          <InputField
            currentStepInputTag={currentStepInputTag}
            inputSettings={thisInputsSettings}
            norm={norm}
            percentile={percentile}
            inputValue={safeTag.value}
            optOutReason={safeTag.optOutReason}
            captureModeIsActive={captureModeIsActive}
            editingEvalModeIsActive={editingEvalModeIsActive}
            handleInputValueChange={handleInputValueChange}
            handleInputValueValidityChange={handleInputValueValidityChange}
            handleOptOutReasonChange={handleOptOutReasonChange}
            showNorms={showNorms}
            currentStepName={currentStepName}
          />
          {!captureModeIsActive && !editingEvalModeIsActive && !tag && (
            <WarningText>No saved value; showing blank.</WarningText>
          )}
        </React.Fragment>
      );
    });
  }, [currentStepInputGroup, INPUT_SETTINGS, inputValuesObject, norms, programKey, ageGroup, athleteGender, captureModeIsActive, editingEvalModeIsActive, handleInputValueChange, handleInputValueValidityChange, handleOptOutReasonChange, showNorms, currentStepName]);

  const chartOptions = useMemo(() => createRadarChart(norms, programKey, ageGroup, athleteGender, 
    inputValuesObject, inputGroupingsObject, 
    INPUT_SETTINGS, steps, activeStep), [norms, programKey, ageGroup, athleteGender, inputValuesObject, inputGroupingsObject, INPUT_SETTINGS, steps, activeStep]);

  const handleNotesChange = useCallback((event) => {
    const notesKey = `notes_${currentStepName.toLowerCase().replace(' ', '_')}`;
    handleInputValueChange(notesKey, event.target.value === '' ? ' ' : event.target.value);
  }, [currentStepName, handleInputValueChange]);

  return (
    <InputGroupingContainer>
      <InputColumn>
        {renderInputFields}
      </InputColumn>
      
      { showNotes ?
      <NotesColumn>
        <NotesLabel>Notes</NotesLabel>
        <NotesTextarea
          value={inputValuesObject?.[`notes_${currentStepName.toLowerCase().replace(' ', '_')}`]?.value || ''}
          onChange={handleNotesChange}
          disabled={!captureModeIsActive && !editingEvalModeIsActive}
          placeholder={!captureModeIsActive && !editingEvalModeIsActive ? "-" : "Enter notes here"}
        />
      </NotesColumn>
      :
      <ChartColumn>
        {currentStepName !== 'Anthropometry' && currentStepInputGroup.length > 2 && (
          <ReactApexChart options={chartOptions} series={chartOptions.series} height={500} type="radar" />
        )}
      </ChartColumn>
      }
    </InputGroupingContainer>
  );
};

export default React.memo(InputGroupings);